import {EventEmitter, Injectable} from '@angular/core';
import {Tab} from '../data/tab';
import {ElectronService} from 'ngx-electron';
import {environment} from '../../../environments/environment';
import {LoggerService} from '../../logging/services/logger.service';
import {LogLevel} from '../../logging/data/logLevel';
import {LogSourceType} from '../../logging/data/logSourceType';
import {LogSource} from '../../logging/data/logSource';

@Injectable({
  providedIn: 'root'
})
export class TabbingService {

  private _visibleTab: Tab;
  tabs: Tab[];

  tabsLoaded: EventEmitter<Tab[]> = new EventEmitter<Tab[]>();

  get visibleTab(): Tab {
    return this._visibleTab;
  }

  get isHeadless(): boolean {
    return window.location.pathname.includes('/add');
  }

  set visibleTab(value: Tab) {
    this._visibleTab = value;
  }

  constructor(private electronService: ElectronService,
              private loggerService: LoggerService) {
  }

  restoreCachedTabs(tenantId: string) {
    if (this.isHeadless) {
      return;
    }
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.TabbingService, 'Restoring cached tabs');
    const json = localStorage.getItem('tabs');
    if (!json) {
      this.loggerService.logMessage(LogLevel.WARN, LogSourceType.SERVICE, LogSource.TabbingService, 'No cached tabs found');
      this.initializeDefaultTab();
      return;
    }
    const tenantSplit = JSON.parse(json);
    const tenantData = tenantSplit[tenantId];
    if (!tenantData) {
      this.loggerService.logMessage(LogLevel.WARN, LogSourceType.SERVICE, LogSource.TabbingService, 'No cached tabs found for tenant');
      this.initializeDefaultTab();
      return;
    }
    const visibleTabIndex = tenantData.visibleIndex;
    this.tabs = tenantData.tabs;
    this.visibleTab = this.tabs[visibleTabIndex];
    for (const tab of this.tabs) {
      tab.navigationData.route = {
        path: tab.navigationData.url,
      };
    }
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.TabbingService, 'Restored cached tabs');
    setTimeout(() => {
      this.tabsLoaded.emit(this.tabs);
    }, 0);
  }

  private initializeDefaultTab() {
    this.tabs = [];
    const newTab = this.addTab();
    newTab.navigationData.route = {
      path: '/home',
    };
    setTimeout(() => {
      this.tabsLoaded.emit(this.tabs);
    }, 0);
  }

  saveTabsToCache(tenantId: string) {
    if (this.isHeadless) {
      return;
    }
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.TabbingService, 'Saving tabs to cache');
    const tenantData = {
      visibleIndex: this.tabs.indexOf(this.visibleTab),
      tabs: this.tabs,
    };
    const savedJson = localStorage.getItem('tabs');
    let tenantSplit;
    if (savedJson) {
      tenantSplit = JSON.parse(savedJson);
    } else {
      tenantSplit = {};
    }
    tenantSplit[tenantId] = tenantData;
    const newJson = JSON.stringify(tenantSplit);
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.TabbingService, 'Saved tabs to cache');
  }

  interceptClickEvent(e) {
    if (!this.electronService.isElectronApp) {
      return;
    }
    let href;
    let launchedUrl = false;
    for (const element of e.path) {
      if (element.tagName === 'A' || element.tagName === 'a') {
        href = element.getAttribute('href');
        if (href.indexOf(environment.frontendUrl + 'docs') === 0) {
          const tab: Tab = new Tab();
          tab.navigationData = href.substr(href.indexOf('/docs') + 1);
          tab.header = 'Docs';
          this.tabs.push(tab);
          this.visibleTab = tab;
          launchedUrl = true;
          e.preventDefault();
          break;
        }
      }
    }
    if (!launchedUrl) {
      this.electronService.shell.openExternal(href);
      e.preventDefault();
    }
  }

  // tslint:disable-next-line:no-unnecessary-initializer
  addTab(tab: Tab | undefined = undefined) {
    if (tab === undefined) {
      tab = Tab.getDefault();
    }
    this.tabs.push(tab);
    this.visibleTab = tab;
    return tab;
  }
}
